import React, { useState, useEffect } from "react";
import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import setStyles from "../../setStyles";
import { useDispatch } from "react-redux";
import { setupPassword } from "../../api/auth";
import Hero from "./Hero";
import { useParams, Redirect } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import history from "../../history";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    height: "100vh",
  },
  containerPassword: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      marginTop: "34px",
    },
  },
  h1: {
    fontSize: "36px",
    ...setStyles.nunitoSansBold,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  h2: {
    fontSize: "24px",
    ...setStyles.nunitoSansBold,
    marginBottom: "20px",
  },
  form: {
    textAlign: "center",
  },
  inputContainer: {
    textAlign: "left",
    margin: "38px auto",
    [theme.breakpoints.down("sm")]: {
      margin: "0px auto 15px",
    },
  },
  h6: {
    padding: "0px 0px 10px 25px",
    fontSize: "12px",
  },
  input: {
    width: "380px",
    height: "61px",
    boxShadow: "0px 4px 10px #05070B33",
    borderRadius: "30px",
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      width: "302px",
    },
  },
  password: {
    width: "90%",
    height: "30px",
    margin: "auto",
    border: "none",
    fontSize: "18px",
    ...setStyles.nunitoSansRegular,
  },
  button: {
    width: "106px",
    height: "35px",
    backgroundColor: "#FF002B",
    borderRadius: "18px",
    border: "none",
    ...setStyles.nunitoSansBold,
    fontSize: "17px",
    color: "#FFFFFF",
    cursor: "pointer",
    [theme.breakpoints.down("sm")]: {
      marginTop: "24px",
    },
  },
  p: {
    fontSize: "16px",
    opacity: 0.5,
    position: "absolute",
    bottom: 50,
    cursor: "pointer",
    [theme.breakpoints.down("sm")]: {
      bottom: 15,
    },
  },
  loginButton: {
    width: "180px",
    height: "45px",
    backgroundColor: "#FF002B",
    borderRadius: "22px",
    border: "none",
    ...setStyles.nunitoSansBold,
    fontSize: "17px",
    color: "#FFFFFF",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "30px auto",
    boxShadow: "0px 4px 10px rgba(255, 0, 43, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#E0001F",
      transform: "translateY(-2px)",
      boxShadow: "0px 6px 12px rgba(255, 0, 43, 0.4)",
    },
  },
  loginIcon: {
    marginLeft: "10px",
    width: "16px",
    height: "16px",
  },
  successContainer: {
    textAlign: "center",
    padding: "20px",
    borderRadius: "10px",
    backgroundColor: "rgba(240, 240, 240, 0.5)",
    backdropFilter: "blur(5px)",
    boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
    margin: "0 20px",
  },
  errorMessage: {
    color: "#FF002B",
    fontSize: "14px",
    margin: "10px 0",
  },
  passwordRequirements: {
    textAlign: "left",
    fontSize: "12px",
    margin: "0 auto 20px",
    width: "380px",
    padding: "0 25px",
    [theme.breakpoints.down("sm")]: {
      width: "302px",
    },
  },
  requirement: {
    margin: "4px 0",
    color: "#555",
  },
  valid: {
    color: "green",
  },
  invalid: {
    color: "#FF002B",
  },
  disabledButton: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  checkboxContainer: {
    display: "flex",
    alignItems: "flex-start",
    margin: "0 auto 20px",
    width: "380px",
    padding: "0 25px",
    [theme.breakpoints.down("sm")]: {
      width: "302px",
    },
  },
  checkbox: {
    marginRight: "10px",
    marginTop: "4px",
    width: "18px",
    height: "18px",
    accentColor: "#FF002B",
  },
  checkboxLabel: {
    fontSize: "14px",
    lineHeight: "1.5",
    textAlign: "left",
  },
}));

const SetupPassword = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    match: false,
  });

  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const [playerName, setPlayerName] = useState("");
  const [agentName, setAgentName] = useState("");

  useEffect(() => {
    setRequirements({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      match: password === confirmPassword && password.length > 0,
    });
  }, [password, confirmPassword]);

  const [isValidToken, setIsValidToken] = useState(true);

  useEffect(() => {
    const fetchNamesFromToken = (token) => {
      try {
        const decodedToken = jwtDecode(token);

        if (!decodedToken) {
          setIsValidToken(false);
          return;
        }

        setPlayerName(decodedToken.playerName);
        setAgentName(decodedToken.agentName);
      } catch (error) {
        console.error("Error decoding token:", error);
        setIsValidToken(false);
      }
    };

    if (token) {
      fetchNamesFromToken(token);
    } else {
      setIsValidToken(false);
    }
  }, [token]);

  const allRequirementsMet =
    Object.values(requirements).every((req) => req === true) && agreeToTerms;

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError("");
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setError("");
  };

  const onSetup = async (e) => {
    e.preventDefault();

    if (!allRequirementsMet) {
      setError("Please ensure all password requirements are met");
      return;
    }

    try {
      setLoading(true);
      await dispatch(setupPassword({ password, confirmPassword, token }));
      setLoading(false);
      history.push("/");
    } catch (error) {
      setLoading(false);
      setError(error.message || "An error occurred. Please try again.");
    }
  };

  if (!isValidToken) {
    return <Redirect to="/login" />;
  }

  return (
    <Grid container alignContent="flex-start" className={classes.container}>
      <Hero />
      <Grid item sm={12} md={6} className={classes.containerPassword}>
        <h1 className={classes.h1}>Setup Your Password</h1>
        <>
            <form className={classes.form}>
              <div className={classes.inputContainer}>
                <h6 className={classes.h6}>NEW PASSWORD</h6>
                <div className={classes.input}>
                  <input
                    autoComplete="new-password"
                    type="password"
                    className={classes.password}
                    value={password}
                    onChange={handlePasswordChange}
                  />
                </div>
              </div>
              <div className={classes.inputContainer}>
                <h6 className={classes.h6}>CONFIRM PASSWORD</h6>
                <div className={classes.input}>
                  <input
                    autoComplete="confirm-password"
                    type="password"
                    className={classes.password}
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                  />
                </div>
              </div>

              <div className={classes.passwordRequirements}>
                <div
                  className={`${classes.requirement} ${
                    requirements.length ? classes.valid : classes.invalid
                  }`}
                >
                  • At least 8 characters
                </div>
                <div
                  className={`${classes.requirement} ${
                    requirements.uppercase ? classes.valid : classes.invalid
                  }`}
                >
                  • At least one uppercase letter
                </div>
                <div
                  className={`${classes.requirement} ${
                    requirements.lowercase ? classes.valid : classes.invalid
                  }`}
                >
                  • At least one lowercase letter
                </div>
                <div
                  className={`${classes.requirement} ${
                    requirements.number ? classes.valid : classes.invalid
                  }`}
                >
                  • At least one number
                </div>
                <div
                  className={`${classes.requirement} ${
                    requirements.match ? classes.valid : classes.invalid
                  }`}
                >
                  • Passwords match
                </div>
              </div>

              <div className={classes.checkboxContainer}>
                <input
                  type="checkbox"
                  id="agreeTerms"
                  className={classes.checkbox}
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                />
                <label htmlFor="agreeTerms" className={classes.checkboxLabel}>
                  I, <strong>{playerName}</strong>, agree to become{" "}
                  <strong>{agentName}'s</strong> prospect.
                </label>
              </div>

              {error && <div className={classes.errorMessage}>{error}</div>}

              <button
                className={`${classes.button} ${
                  !allRequirementsMet || loading ? classes.disabledButton : ""
                }`}
                onClick={onSetup}
                disabled={!allRequirementsMet || loading}
              >
                {loading ? "Setting..." : "Submit"}
              </button>
            </form>
            <p
              className={classes.p}
              onClick={() => history.push("/")}
            >
              Login &gt;
            </p>
          </>
      </Grid>
    </Grid>
  );
};

export default SetupPassword;
